import { baseApi } from "@/shared/api";
import type {
  AnnualLeaveResponse,
  UnpaidLeaveResponse,
  MedicalLeaveResponse,
  CombinedLeaveItem,
  CreateAnnualLeaveDto,
  CreateUnpaidLeaveDto,
  CreateMedicalLeaveDto,
  UpdateAnnualLeaveDto,
  UpdateUnpaidLeaveDto,
  UpdateMedicalLeaveDto,
  BusinessTripDetailResponse,
} from "../types";

export const leavesApi = baseApi.injectEndpoints({
  endpoints(builder) {
    return {
      getCombinedLeaveApplications: builder.query<CombinedLeaveItem[], void>({
        query() {
          return {
            url: "/api/hr/leave-applications/",
            method: "GET",
            credentials: "include",
          };
        },
        providesTags: [{ type: "LeaveApplications" as const, id: "LIST" }],
      }),
      // DEPRECATED: use getCombinedLeaveApplications instead
      getAnnualLeaves: builder.query<AnnualLeaveResponse[], void>({
        query() {
          return {
            url: "/api/hr/annual-leaves/",
            method: "GET",
            credentials: "include",
          };
        },
        providesTags: [{ type: "LeaveApplications" as const, id: "LIST" }],
      }),
      // DEPRECATED: use getCombinedLeaveApplications instead
      getUnpaidLeaves: builder.query<UnpaidLeaveResponse[], void>({
        query() {
          return {
            url: "/api/hr/unpaid-leaves/",
            method: "GET",
            credentials: "include",
          };
        },
        providesTags: [{ type: "LeaveApplications" as const, id: "LIST" }],
      }),
      // DEPRECATED: use getCombinedLeaveApplications instead
      getMedicalLeaves: builder.query<MedicalLeaveResponse[], void>({
        query() {
          return {
            url: "/api/hr/medical-leaves/",
            method: "GET",
            credentials: "include",
          };
        },
        providesTags: [{ type: "LeaveApplications" as const, id: "LIST" }],
      }),
      getAnnualLeave: builder.query<AnnualLeaveResponse, number>({
        query(id) {
          return {
            url: `/api/hr/annual-leaves/${id}/`,
            method: "GET",
            credentials: "include",
          };
        },
        providesTags(_result, _error, id) {
          return [{ type: "LeaveApplications" as const, id }];
        },
      }),
      getUnpaidLeave: builder.query<UnpaidLeaveResponse, number>({
        query(id) {
          return {
            url: `/api/hr/unpaid-leaves/${id}/`,
            method: "GET",
            credentials: "include",
          };
        },
        providesTags(_result, _error, id) {
          return [{ type: "LeaveApplications" as const, id }];
        },
      }),
      getMedicalLeave: builder.query<MedicalLeaveResponse, number>({
        query(id) {
          return {
            url: `/api/hr/medical-leaves/${id}/`,
            method: "GET",
            credentials: "include",
          };
        },
        providesTags(_result, _error, id) {
          return [{ type: "LeaveApplications" as const, id }];
        },
      }),
      createAnnualLeave: builder.mutation<AnnualLeaveResponse, CreateAnnualLeaveDto>({
        query(body) {
          return {
            url: "/api/hr/annual-leaves/",
            method: "POST",
            body,
            credentials: "include",
          };
        },
        invalidatesTags: [
          { type: "LeaveApplications" as const, id: "LIST" },
          { type: "SelfService" as const, id: "REQUESTS" },
        ],
      }),
      createUnpaidLeave: builder.mutation<UnpaidLeaveResponse, CreateUnpaidLeaveDto>({
        query(body) {
          return {
            url: "/api/hr/unpaid-leaves/",
            method: "POST",
            body,
            credentials: "include",
          };
        },
        invalidatesTags: [
          { type: "LeaveApplications" as const, id: "LIST" },
          { type: "SelfService" as const, id: "REQUESTS" },
        ],
      }),
      createMedicalLeave: builder.mutation<MedicalLeaveResponse, CreateMedicalLeaveDto>({
        query(body) {
          return {
            url: "/api/hr/medical-leaves/",
            method: "POST",
            body,
            credentials: "include",
          };
        },
        invalidatesTags: [
          { type: "LeaveApplications" as const, id: "LIST" },
          { type: "SelfService" as const, id: "REQUESTS" },
        ],
      }),
      updateAnnualLeave: builder.mutation<AnnualLeaveResponse, { id: number; data: UpdateAnnualLeaveDto }>({
        query({ id, data }) {
          return {
            url: `/api/hr/annual-leaves/${id}/`,
            method: "PATCH",
            body: data,
            credentials: "include",
          };
        },
        invalidatesTags(_result, _error, { id }) {
          return [
            { type: "LeaveApplications" as const, id },
            { type: "LeaveApplications" as const, id: "LIST" },
          ];
        },
      }),
      updateUnpaidLeave: builder.mutation<UnpaidLeaveResponse, { id: number; data: UpdateUnpaidLeaveDto }>({
        query({ id, data }) {
          return {
            url: `/api/hr/unpaid-leaves/${id}/`,
            method: "PATCH",
            body: data,
            credentials: "include",
          };
        },
        invalidatesTags(_result, _error, { id }) {
          return [
            { type: "LeaveApplications" as const, id },
            { type: "LeaveApplications" as const, id: "LIST" },
          ];
        },
      }),
      updateMedicalLeave: builder.mutation<MedicalLeaveResponse, { id: number; data: UpdateMedicalLeaveDto }>({
        query({ id, data }) {
          return {
            url: `/api/hr/medical-leaves/${id}/`,
            method: "PATCH",
            body: data,
            credentials: "include",
          };
        },
        invalidatesTags(_result, _error, { id }) {
          return [
            { type: "LeaveApplications" as const, id },
            { type: "LeaveApplications" as const, id: "LIST" },
          ];
        },
      }),
      deleteAnnualLeave: builder.mutation<void, number>({
        query(id) {
          return {
            url: `/api/hr/annual-leaves/${id}/`,
            method: "DELETE",
            credentials: "include",
          };
        },
        invalidatesTags: [
          { type: "LeaveApplications" as const, id: "LIST" },
          { type: "SelfService" as const, id: "REQUESTS" },
        ],
      }),
      deleteUnpaidLeave: builder.mutation<void, number>({
        query(id) {
          return {
            url: `/api/hr/unpaid-leaves/${id}/`,
            method: "DELETE",
            credentials: "include",
          };
        },
        invalidatesTags: [
          { type: "LeaveApplications" as const, id: "LIST" },
          { type: "SelfService" as const, id: "REQUESTS" },
        ],
      }),
      deleteMedicalLeave: builder.mutation<void, number>({
        query(id) {
          return {
            url: `/api/hr/medical-leaves/${id}/`,
            method: "DELETE",
            credentials: "include",
          };
        },
        invalidatesTags: [
          { type: "LeaveApplications" as const, id: "LIST" },
          { type: "SelfService" as const, id: "REQUESTS" },
        ],
      }),
      acknowledgeMedicalLeave: builder.mutation<MedicalLeaveResponse, number>({
        query(id) {
          return {
            url: `/api/hr/medical-leaves/${id}/acknowledge/`,
            method: "POST",
            credentials: "include",
          };
        },
        invalidatesTags(_result, _error, id) {
          return [
            { type: "LeaveApplications" as const, id },
            { type: "LeaveApplications" as const, id: "LIST" },
            "PendingActions",
          ];
        },
      }),
      closeMedicalLeave: builder.mutation<
        MedicalLeaveResponse,
        { id: number; endDate: string; file?: File; sickNoteNumber?: string }
      >({
        query({ id, endDate, file, sickNoteNumber }) {
          const form = new FormData();
          form.append("end_date", endDate);
          if (file) form.append("file", file);
          if (sickNoteNumber) form.append("sick_note_number", sickNoteNumber);
          return {
            url: `/api/hr/medical-leaves/${id}/close/`,
            method: "POST",
            body: form,
            credentials: "include",
          };
        },
        invalidatesTags(_result, _error, { id }) {
          return [
            { type: "LeaveApplications" as const, id },
            { type: "LeaveApplications" as const, id: "LIST" },
            "PendingActions",
          ];
        },
      }),
      uploadMedicalNote: builder.mutation<
        MedicalLeaveResponse,
        { id: number; file: File; sickNoteNumber?: string }
      >({
        query({ id, file, sickNoteNumber }) {
          const form = new FormData();
          form.append("file", file);
          if (sickNoteNumber) form.append("sick_note_number", sickNoteNumber);
          return {
            url: `/api/hr/medical-leaves/${id}/upload-note/`,
            method: "POST",
            body: form,
            credentials: "include",
          };
        },
        invalidatesTags(_result, _error, { id }) {
          return [
            { type: "LeaveApplications" as const, id },
            { type: "LeaveApplications" as const, id: "LIST" },
          ];
        },
      }),
      updateMedicalLeaveDates: builder.mutation<
        MedicalLeaveResponse,
        { id: number; data: { start_date?: string; end_date?: string } }
      >({
        query({ id, data }) {
          return {
            url: `/api/hr/medical-leaves/${id}/`,
            method: "PATCH",
            body: data,
            credentials: "include",
          };
        },
        invalidatesTags(_result, _error, { id }) {
          return [
            { type: "LeaveApplications" as const, id },
            { type: "LeaveApplications" as const, id: "LIST" },
            "PendingActions",
          ];
        },
      }),
      getHrBusinessTripDetail: builder.query<BusinessTripDetailResponse, number>({
        query(id) {
          return {
            url: `/api/hr/business-trips/${id}/`,
            method: "GET",
            credentials: "include" as const,
          };
        },
        providesTags(_result, _error, id) {
          return [{ type: "BusinessTrips" as const, id }];
        },
      }),
    };
  },
});

export const {
  useGetCombinedLeaveApplicationsQuery,
  useGetAnnualLeavesQuery,
  useGetUnpaidLeavesQuery,
  useGetMedicalLeavesQuery,
  useGetAnnualLeaveQuery,
  useGetUnpaidLeaveQuery,
  useGetMedicalLeaveQuery,
  useCreateAnnualLeaveMutation,
  useCreateUnpaidLeaveMutation,
  useCreateMedicalLeaveMutation,
  useUpdateAnnualLeaveMutation,
  useUpdateUnpaidLeaveMutation,
  useUpdateMedicalLeaveMutation,
  useDeleteAnnualLeaveMutation,
  useDeleteUnpaidLeaveMutation,
  useDeleteMedicalLeaveMutation,
  useAcknowledgeMedicalLeaveMutation,
  useCloseMedicalLeaveMutation,
  useUploadMedicalNoteMutation,
  useUpdateMedicalLeaveDatesMutation,
  useGetHrBusinessTripDetailQuery,
} = leavesApi;

